import React from 'react';
import { Badge } from '../ui/badge';
import { Status } from '../../types/ui';
import { BarChart3, X } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';

interface Column {
  key: string;
  header: string;
}

interface RelatedItems {
  title: string;
  items: any[];
  columns: Column[];
}

interface BaseItem {
  id: string;
  label: string;
  description?: string;
}

// Performance framework properties common across most object types (except Product)
interface PerformanceMetrics {
  timePercentage: number;
  volumePercentage: number;
  qualityPercentage: number;
  sentimentPercentage: number;
  costPercentage: number;
  riskPercentage: number;
  healthPercentage: number; // Calculated as average of other percentages (excluding zeros)
}

// Base interface for items that include performance metrics
interface PerformanceItem extends BaseItem, PerformanceMetrics {}

interface StageItem extends PerformanceItem {
  sortOrder: number;
  capabilities: Array<{
    id: string;
    name: string;
    timeDimension: string;
    type: string;
  }>;
}

interface CapabilityItem extends PerformanceItem {
  stageId?: string;
  stageName?: string;
  type?: string;
  metrics?: number;
}

interface OrganisationUnitItem extends PerformanceItem {
  type: string;
}

type SelectedItem = BaseItem | PerformanceItem | StageItem | CapabilityItem | OrganisationUnitItem;

interface DataManagementListProps {
  loading: boolean;
  items: any[];
  selectedId: string | null;
  onItemClick: (id: string) => void;
  columns: Column[];
  selectedItem?: SelectedItem | null;
  itemType?: 'stage' | 'capability' | 'application' |'valuestream' | 'team' | 'product' | 'activity' | 'outcome';
  relatedItems?: RelatedItems[];
}

export function DataManagementList({
  loading,
  items,
  selectedId,
  onItemClick,
  columns,
  selectedItem,
  itemType = 'stage',
  relatedItems,
}: DataManagementListProps) {
  const [activeTab, setActiveTab] = React.useState<string>('details');

  // Helper function to render cell content
  const renderCellContent = (item: any, key: string) => {
    const value = item[key];
    
    // Special handling for IDs when displayed in table
    if (key === 'id' && typeof value === 'string') {
      // If the ID is a URI or very long, show a truncated version
      if (value.includes('http://') || value.includes('https://') || value.length > 40) {
        const lastPart = value.split('/').pop() || value.split('#').pop() || value;
        return <span className="text-xs font-mono truncate" title={value}>{lastPart.substring(0, 20)}...</span>;
      }
      return <span className="text-xs font-mono">{value}</span>;
    }
    
    // Special handling for label content to make sure IDs don't leak into labels
    if (key === 'label' && typeof value === 'string') {
      // For capabilities specifically, check if their stage name is in the label
      if (isCapability(item) && item.stageName && value.includes(item.stageName)) {
        // Remove the stage name from the label
        const cleanLabel = value.replace(item.stageName, '').trim();
        return cleanLabel;
      }
      
      // If label contains any ID-looking parts or technical details, clean it
      if (value.includes('(') || value.includes('-') || value.includes('#')) {
        const cleanLabel = value.split('-')[0].split('(')[0].trim();
        return cleanLabel;
      }
      
      return value;
    }
    
    // Standard handling for other fields
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }
    
    return '';
  };

  const isStage = (item: SelectedItem): item is StageItem => {
    return 'sortOrder' in item && 'capabilities' in item;
  };

  const isCapability = (item: SelectedItem): item is CapabilityItem => {
    return 'stageName' in item && 'stageId' in item;
  };

  // Find related metrics for the selected item
  const getRelatedMetrics = () => {
    return [];
  };

  const relatedMetrics = getRelatedMetrics();

  if (loading && !selectedItem) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-6 min-h-screen">
      <div className={`flex-1 ${selectedItem ? 'w-[60%]' : 'w-full'}`}>
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key}>{column.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow 
                key={item.id} 
                className={`cursor-pointer hover:bg-secondary/20 ${selectedId === item.id ? 'bg-secondary/30' : ''}`}
                onClick={() => onItemClick(item.id)}
              >
                {columns.map((column) => (
                  <TableCell 
                    key={`${item.id}-${column.key}`} 
                    className={column.key === 'label' ? 'font-medium' : ''}
                  >
                    {renderCellContent(item, column.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      {selectedItem && (
        <div className="w-[40%] h-[calc(100vh-2rem)]">
          {loading ? (
            <Card className="sticky top-4 h-full overflow-hidden flex flex-col">
              <CardHeader className="pb-3 shrink-0 border-b">
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <div className="p-4 space-y-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-20 w-full" />
              </div>
            </Card>
          ) : (
            <Card className="sticky top-4 h-full overflow-hidden flex flex-col">
              <CardHeader className="pb-3 shrink-0 border-b">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg font-semibold">{selectedItem.label}</CardTitle>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="secondary">
                        {itemType === 'stage' ? 'Value Stream Stage' : 'Business Capability'}
                      </Badge>
                    </div>
                    {selectedItem.description && (
                      <CardDescription className="mt-2 text-sm">
                        {selectedItem.description}
                      </CardDescription>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onItemClick('')}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className="grid w-full grid-cols-2 px-4 shrink-0">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="metrics" className="flex items-center gap-2">
                  <span>Metrics</span>
                  <Badge variant="secondary" className="ml-1">
                    {relatedMetrics.length}
                  </Badge>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent 
                value="details" 
                className="flex-1 overflow-y-auto scrollbar-hide"
              >
                <CardContent className="space-y-6 px-4">
                  {/* ID */}
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      {itemType === 'stage' ? 'Stage ID' : 'Capability ID'}
                    </h4>
                    <p className="text-xs font-mono bg-slate-50 p-2 rounded-md break-all overflow-x-auto" 
                      style={{ maxHeight: '100px', overflowY: 'auto' }}>
                      {selectedItem.id}
                    </p>
                  </div>

                  {/* Performance Metrics - only for items that have them (not products) */}
                  {((selectedItem as any).metrics || (selectedItem as any).metricsObj) && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">Performance Metrics</h4>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="bg-slate-50 p-3 rounded-md">
                          <div className="text-xs text-muted-foreground mb-1">Time</div>
                          <div className="text-lg font-semibold">{((selectedItem as any).metrics || (selectedItem as any).metricsObj).TimePercentage}%</div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-md">
                          <div className="text-xs text-muted-foreground mb-1">Volume</div>
                          <div className="text-lg font-semibold">{((selectedItem as any).metrics || (selectedItem as any).metricsObj).VolumePercentage}%</div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-md">
                          <div className="text-xs text-muted-foreground mb-1">Quality</div>
                          <div className="text-lg font-semibold">{((selectedItem as any).metrics || (selectedItem as any).metricsObj).QualityPercentage}%</div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-md">
                          <div className="text-xs text-muted-foreground mb-1">Sentiment</div>
                          <div className="text-lg font-semibold">{((selectedItem as any).metrics || (selectedItem as any).metricsObj).SentimentPercentage}%</div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-md">
                          <div className="text-xs text-muted-foreground mb-1">Cost</div>
                          <div className="text-lg font-semibold">{((selectedItem as any).metrics || (selectedItem as any).metricsObj).CostPercentage}%</div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-md">
                          <div className="text-xs text-muted-foreground mb-1">Risk</div>
                          <div className="text-lg font-semibold">{((selectedItem as any).metrics || (selectedItem as any).metricsObj).RiskPercentage}%</div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-md col-span-2 border-2 border-green-200 bg-green-50">
                          <div className="text-xs text-muted-foreground mb-1">Health Score</div>
                          <div className="text-xl font-bold text-green-700">{((selectedItem as any).metrics || (selectedItem as any).metricsObj).HealthPercentage}%</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Stage-specific content */}
                  {isStage(selectedItem) && (
                    <>
                      {/* Sort Order */}
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Sort Order</h4>
                        <p className="text-sm">{selectedItem.sortOrder || 'N/A'}</p>
                      </div>

                      {/* Business Capabilities */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-sm font-medium text-muted-foreground">Business Capabilities</h4>
                          <Badge variant="secondary">
                            {selectedItem.capabilities.length}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {selectedItem.capabilities.map((capability: any) => (
                            <div key={capability.id} className="bg-slate-50 p-2 rounded-md">
                              <div className="text-sm font-medium">
                                {capability.name}
                              </div>
                              {capability.timeDimension && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {capability.timeDimension}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Capability, Team, Application, Outcome, Product, and Activity shared content */}
                  {(itemType === 'capability' || itemType === 'team' || itemType === 'application' || itemType === 'outcome' || itemType === 'product' || itemType === 'activity') && (
                    <>
                      {isCapability(selectedItem) && (
                        <div>
                          <h4 className="text-sm font-medium text-muted-foreground mb-1">Associated Stage</h4>
                          <p className="text-sm">{selectedItem.stageName}</p>
                        </div>
                      )}
                      
                      {/* Related Items (Teams for Capability, Applications for Team) */}
                      {selectedItem && relatedItems && relatedItems.length > 0 && (
                        <Tabs defaultValue={relatedItems[0].title} className="mt-4">
                          <TabsList className={`grid w-full grid-cols-${relatedItems.length} h-8`}>
                            {relatedItems.map(group => (
                              <TabsTrigger key={group.title} value={group.title} className="text-xs px-2 py-1 h-full">
                                {group.title}
                              </TabsTrigger>
                            ))}
                          </TabsList>
                          {relatedItems.map(group => (
                            <TabsContent key={group.title} value={group.title}>
                              <div className="border rounded-md overflow-hidden mt-2">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      {group.columns.map((col) => (
                                        <TableHead key={col.key} className="h-8 px-2 py-1 text-xs">
                                          {col.header}
                                        </TableHead>
                                      ))}
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {group.items.map((item) => (
                                      <TableRow key={item.id} className="h-8 hover:bg-secondary/20">
                                        {group.columns.map((col) => (
                                          <TableCell key={`${item.id}-${col.key}`} className="px-2 py-1 text-xs">
                                            {renderCellContent(item, col.key)}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </TabsContent>
                          ))}
                        </Tabs>
                      )}
                    </>
                  )}
                </CardContent>
              </TabsContent>
              
              <TabsContent 
                value="metrics" 
                className="flex-1 overflow-y-auto scrollbar-hide"
              >
                <CardContent className="space-y-3 px-4 py-4">
                  {relatedMetrics.length > 0 ? (
                    relatedMetrics.map((metric, index) => (
                      <Card key={index} className="shadow-sm hover:shadow-md transition-all duration-200 bg-slate-50/50">
                        <CardHeader className="p-3 pb-2">
                          <CardTitle className="text-sm font-medium leading-tight">{metric["KPI Name"]}</CardTitle>
                          <CardDescription className="text-xs mt-1">
                            {metric["KPI Description"]}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="p-3 pt-0">
                          <div className="text-xs text-muted-foreground">
                            <div className="flex items-center gap-1.5 mb-1">
                              <BarChart3 className="h-3 w-3" />
                              <span className="font-medium">Formula:</span>
                              <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">
                                {metric["KPI Formula"]}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                        <CardFooter className="p-3 pt-2 flex justify-between items-center text-xs border-t border-slate-200">
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Type:</span>
                            <Badge variant="secondary" className="text-[10px] bg-slate-200">
                              {metric["Operation"] || "Custom"}
                            </Badge>
                          </div>
                          <Badge variant="outline" className="text-[10px]">
                            {metric["KPI Objects"]}
                          </Badge>
                        </CardFooter>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      No metrics found for this {itemType}
                    </div>
                  )}
                </CardContent>
              </TabsContent>
            </Tabs>
          </Card>
          )}
        </div>
      )}
    </div>
  );
}